/**
 * Header Mega Menu
 * Handles desktop dropdown and mobile/tablet sliding menu functionality
 */

class HeaderMegaMenu {
	constructor() {
		this.megaMenu = document.querySelector( '.js-mega-menu' );
		this.overlay = document.querySelector( '.js-mega-menu-overlay' );
		this.toggle = document.querySelector( '.js-mega-menu-toggle' );
		this.closeBtn = document.querySelector( '.js-mega-menu-close' );
		this.desktopMenuItems = document.querySelectorAll( '.js-mega-menu-item' );
		this.desktopDropdowns = document.querySelectorAll( '.mega-menu__dropdown' );
		this.mobileMenuItems = document.querySelectorAll( '.js-mega-menu-item-mobile' );
		this.level1 = document.querySelector( '.js-mega-menu-level-1' );
		this.level2Panels = document.querySelectorAll( '.js-mega-menu-level-2' );
		this.level3Panels = document.querySelectorAll( '.js-mega-menu-level-3' );
		this.backButtons = document.querySelectorAll( '.js-mega-menu-back' );
		this.backToLevel2Buttons = document.querySelectorAll( '.js-mega-menu-back-to-level-2' );
		this.level2GroupButtons = document.querySelectorAll( '.js-mega-menu-level-2-group' );

		if ( !this.megaMenu || !this.toggle ) return;

		this.isDesktop = window.matchMedia( '(min-width: 960px)' ).matches;
		this.currentLevel2 = null;
		this.currentLevel3 = null;

		this.init();
	}

	init() {
		this.bindEvents();
	}

	bindEvents() {
		// Toggle button click
		this.toggle.addEventListener( 'click', ( e ) => {
			e.preventDefault();
			const isOpen = this.megaMenu.classList.contains( 'is-open' );

			if ( isOpen ) {
				this.closeMegaMenu();
			} else {
				this.openMegaMenu();
			}
		} );

		// Close button click
		if ( this.closeBtn  ) {
			this.closeBtn.addEventListener( 'click', ( e ) => {
				e.preventDefault();
				this.closeMegaMenu();
			} );
		}

		// Overlay click
		this.overlay.addEventListener( 'click', ( e ) => {
			e.preventDefault();
			if ( this.isDesktop ) {
				this.closeAllDropdowns();
			} else {
				this.closeMegaMenu();
			}
		} );

		// Desktop menu items hover - track active dropdown
		this.activeDropdown = null;
		this.activeDropdownItem = null;
		this.closeTimeout = null;

		this.desktopMenuItems.forEach( item => {
			const parentLi = item.closest( '.mega-menu__desktop-item' );
			const menuId = item.getAttribute( 'data-menu-id' );
			const dropdown = document.getElementById( menuId );

			if ( parentLi && dropdown ) {
				// Allow click to navigate to L1 link - don't prevent default
				item.addEventListener( 'click', () => {
					if ( this.isDesktop ) {
						// Let the browser follow the href
					}
				} );

				parentLi.addEventListener( 'mouseenter', () => {
					if ( this.isDesktop ) {
						// Clear any pending close
						if ( this.closeTimeout ) {
							clearTimeout( this.closeTimeout );
							this.closeTimeout = null;
						}

						// Close previous dropdown if different
						if ( this.activeDropdown && this.activeDropdown !== dropdown ) {
							this.activeDropdown.classList.remove( 'is-open' );
							if ( this.activeDropdownItem ) {
								this.activeDropdownItem.setAttribute( 'aria-expanded', 'false' );
							}
						}

						// Open current dropdown
						this.activeDropdown = dropdown;
						this.activeDropdownItem = item;
						item.setAttribute( 'aria-expanded', 'true' );
						dropdown.classList.add( 'is-open' );
						this.overlay.classList.add( 'is-visible' );

						// Position polygon based on menu item
						this.positionPolygon( item, dropdown );
					}
				} );

				parentLi.addEventListener( 'mouseleave', () => {
					if ( this.isDesktop ) {
						// Delay closing to allow moving to dropdown
						this.closeTimeout = setTimeout( () => {
							if ( this.activeDropdown && !this.activeDropdown.matches( ':hover' ) ) {
								this.activeDropdown.classList.remove( 'is-open' );
								this.overlay.classList.remove( 'is-visible' );
								if ( this.activeDropdownItem ) {
									this.activeDropdownItem.setAttribute( 'aria-expanded', 'false' );
								}
								this.activeDropdown = null;
								this.activeDropdownItem = null;
							}
						}, 150 );
					}
				} );

				// Keep dropdown open when hovering over it
				dropdown.addEventListener( 'mouseenter', () => {
					if ( this.isDesktop && this.closeTimeout ) {
						clearTimeout( this.closeTimeout );
						this.closeTimeout = null;
					}
				} );

				dropdown.addEventListener( 'mouseleave', () => {
					if ( this.isDesktop ) {
						if ( this.activeDropdown ) {
							this.activeDropdown.classList.remove( 'is-open' );
							this.overlay.classList.remove( 'is-visible' );
							if ( this.activeDropdownItem ) {
								this.activeDropdownItem.setAttribute( 'aria-expanded', 'false' );
							}
							this.activeDropdown = null;
							this.activeDropdownItem = null;
						}
					}
				} );
			}
		} );

		// Mobile menu items click
		this.mobileMenuItems.forEach( item => {
			item.addEventListener( 'click', ( e ) => {
				e.preventDefault();
				const menuId = item.getAttribute( 'data-menu-id' );
				this.navigateToLevel2( menuId );
			} );
		} );

		// Back buttons (level 2 to level 1)
		this.backButtons.forEach( button => {
			button.addEventListener( 'click', ( e ) => {
				e.preventDefault();
				this.navigateBackToLevel1();
			} );
		} );

		// Level 2 group buttons (navigate to level 3)
		this.level2GroupButtons.forEach( button => {
			button.addEventListener( 'click', ( e ) => {
				e.preventDefault();
				const groupId = button.getAttribute( 'data-group-id' );
				this.navigateToLevel3( groupId );
			} );
		} );

		// Back to level 2 buttons
		this.backToLevel2Buttons.forEach( button => {
			button.addEventListener( 'click', ( e ) => {
				e.preventDefault();
				this.navigateBackToLevel2();
			} );
		} );

		// ESC key to close
		document.addEventListener( 'keydown', ( e ) => {
			if ( e.key === 'Escape' ) {
				if ( this.isDesktop ) {
					this.closeAllDropdowns();
				} else {
					if ( this.currentLevel3 ) {
						this.navigateBackToLevel2();
					} else if ( this.currentLevel2 ) {
						this.navigateBackToLevel1();
					} else {
						this.closeMegaMenu();
					}
				}
			}
		} );

		// Close dropdowns when clicking outside (desktop)
		document.addEventListener( 'click', ( e ) => {
			if ( this.isDesktop ) {
				const clickedDropdown = e.target.closest( '.mega-menu__dropdown' );
				const clickedMenuItem = e.target.closest( '.js-mega-menu-item' );
				const clickedOverlay = e.target.closest( '.js-mega-menu-overlay' );

				if ( !clickedDropdown && !clickedMenuItem && !clickedOverlay ) {
					this.closeAllDropdowns();
				}
			}
		} );

		// Handle window resize
		window.addEventListener( 'resize', () => this.updateBreakpoint() );

		// Trap focus within mega menu when open on mobile/tablet
		document.addEventListener( 'keydown', ( e ) => this.trapFocus( e ) );

		// Prevent body scroll when menu is open on mobile/tablet
		document.addEventListener( 'touchmove', ( e ) => this.preventBodyScroll( e ), { passive: false } );
	}

	updateBreakpoint() {
		const wasDesktop = this.isDesktop;
		this.isDesktop = window.matchMedia( '(min-width: 960px)' ).matches;

		// If switching from mobile to desktop, close everything
		if ( !wasDesktop && this.isDesktop ) {
			this.closeMegaMenu();
			this.closeAllDropdowns();
		}
	}

	openMegaMenu() {
		this.megaMenu.classList.add( 'is-open' );
		this.overlay.classList.add( 'is-visible' );
		this.megaMenu.setAttribute( 'aria-hidden', 'false' );
		this.overlay.setAttribute( 'aria-hidden', 'false' );
		this.toggle.setAttribute( 'aria-expanded', 'true' );
		document.body.style.overflow = 'hidden';
	}

	closeMegaMenu() {
		this.megaMenu.classList.remove( 'is-open' );
		this.overlay.classList.remove( 'is-visible' );
		this.megaMenu.setAttribute( 'aria-hidden', 'true' );
		this.overlay.setAttribute( 'aria-hidden', 'true' );
		this.toggle.setAttribute( 'aria-expanded', 'false' );
		document.body.style.overflow = '';

		// Reset mobile/tablet navigation
		if ( this.level1 ) {
			this.level1.classList.remove( 'is-hidden' );
		}
		this.level2Panels.forEach( panel => {
			panel.classList.remove( 'is-active' );
		} );
		this.level3Panels.forEach( panel => {
			panel.classList.remove( 'is-active' );
		} );
		this.currentLevel2 = null;
		this.currentLevel3 = null;

		// Close all desktop dropdowns
		this.closeAllDropdowns();
	}

	positionPolygon( menuItem, dropdown ) {
		// Get menu item position
		const itemRect = menuItem.getBoundingClientRect();
		const itemCenter = itemRect.left + ( itemRect.width / 2 );

		// Get dropdown position
		const dropdownRect = dropdown.getBoundingClientRect();
		const dropdownLeft = dropdownRect.left;

		// Calculate polygon position relative to dropdown
		const polygonLeft = itemCenter - dropdownLeft;

		// Set CSS custom property for polygon position
		dropdown.style.setProperty( '--polygon-left', `${polygonLeft}px` );
	}

	closeAllDropdowns() {
		document.querySelectorAll( '.mega-menu__dropdown.is-open' ).forEach( dropdown => {
			dropdown.classList.remove( 'is-open' );
		} );
		this.desktopMenuItems.forEach( item => {
			item.setAttribute( 'aria-expanded', 'false' );
		} );
		this.overlay.classList.remove( 'is-visible' );
	}

	openDropdown( menuId ) {
		const dropdown = document.getElementById( menuId );
		if ( dropdown ) {
			this.closeAllDropdowns();
			dropdown.classList.add( 'is-open' );
			this.overlay.classList.add( 'is-visible' );
		}
	}

	closeDropdown( menuId ) {
		const dropdown = document.getElementById( menuId );
		if ( dropdown ) {
			dropdown.classList.remove( 'is-open' );
			this.overlay.classList.remove( 'is-visible' );
		}
	}

	navigateToLevel2( menuId ) {
		const targetPanel = document.getElementById( `${menuId}-mobile` );
		if ( targetPanel ) {
			this.level1.classList.add( 'is-hidden' );
			targetPanel.classList.add( 'is-active' );
			this.currentLevel2 = targetPanel;
		}
	}

	navigateBackToLevel1() {
		if ( this.currentLevel2 ) {
			this.level1.classList.remove( 'is-hidden' );
			this.currentLevel2.classList.remove( 'is-active' );
			this.currentLevel2 = null;
		}

		// Also close level 3 if open
		if ( this.currentLevel3 ) {
			this.currentLevel3.classList.remove( 'is-active' );
			this.currentLevel3 = null;
		}
	}

	navigateToLevel3( groupId ) {
		const targetPanel = document.getElementById( `${groupId}-mobile` );
		if ( targetPanel ) {
			targetPanel.classList.add( 'is-active' );
			this.currentLevel3 = targetPanel;
		}
	}

	navigateBackToLevel2() {
		if ( this.currentLevel3 ) {
			this.currentLevel3.classList.remove( 'is-active' );
			this.currentLevel3 = null;
		}
	}

	trapFocus( e ) {
		if ( !this.megaMenu.classList.contains( 'is-open' ) || this.isDesktop ) return;

		const focusableElements = this.megaMenu.querySelectorAll(
			'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled])'
		);
		const firstElement = focusableElements[0];
		const lastElement = focusableElements[focusableElements.length - 1];

		if ( e.key === 'Tab' ) {
			if ( e.shiftKey && document.activeElement === firstElement ) {
				e.preventDefault();
				lastElement.focus();
			} else if ( !e.shiftKey && document.activeElement === lastElement ) {
				e.preventDefault();
				firstElement.focus();
			}
		}
	}

	preventBodyScroll( e ) {
		if ( this.megaMenu.classList.contains( 'is-open' ) && !this.isDesktop ) {
			if ( !this.megaMenu.contains( e.target ) ) {
				e.preventDefault();
			}
		}
	}
}

// Initialize when DOM is ready
if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', () => {
		new HeaderMegaMenu();
	} );
} else {
	new HeaderMegaMenu();
}

export default HeaderMegaMenu;
